import { LitElement, type PropertyValues } from "lit";
import { dispatchChromePress } from "./createChromeRoot";
import { attachOutsideClick } from "./outsideClick";
import type { ViewportSnapshot, ViewportStore } from "@slot/presentation";

/** The view before a viewport store is attached (tests, or an element rendered standalone). */
const DEFAULT_VIEW: ViewportSnapshot = { css: { width: 1080, height: 1920 }, design: { width: 1080, height: 1920 }, scale: 1, shape: "mobile" };

/**
 * A DOM chrome panel: a lit element on the chrome root that lays out in design units (inside the
 * root, one CSS pixel is one design unit). The factory attaches the viewport store before mounting;
 * `view` then follows it and the host carries `data-shape`, so per-shape layout is a CSS rule
 * (`:host([data-shape="desktop"])`); the TS fit math stays for the rules CSS cannot express.
 */
export abstract class ChromeElement extends LitElement {
  public static override properties = { view: { state: true } };

  public declare view: ViewportSnapshot;
  /** Attached by the factory before the element is mounted. */
  public viewport: ViewportStore | null = null;
  private unsubscribeViewport?: () => void;
  private detachOutsideClick: (() => void) | null = null;

  public constructor() {
    super();
    this.view = DEFAULT_VIEW;
  }

  public override connectedCallback(): void {
    super.connectedCallback();
    this.unsubscribeViewport = this.viewport?.subscribe((s) => {
      this.view = s;
    });
  }

  protected override willUpdate(changed: PropertyValues): void {
    if (changed.has("view")) this.dataset.shape = this.view.shape;
  }

  public override disconnectedCallback(): void {
    super.disconnectedCallback();
    this.unsubscribeViewport?.();
    this.unsubscribeViewport = undefined;
    this.detachOutsideClick?.();
    this.detachOutsideClick = null;
  }

  protected get isDesktop(): boolean {
    return this.view.shape === "desktop";
  }

  protected get isLandscape(): boolean {
    return this.view.shape === "mobile_landscape";
  }

  /**
   * A desktop popover's dismissal, called from its `updated()`: while it is open on the desktop shape, a
   * press that starts outside it and its `anchor` (the toggle, which closes it through its presenter)
   * calls `onOutside`. Closed, on another shape or disconnected, the document listener is gone. The
   * anchor is read when the listener attaches.
   */
  protected syncOutsideClick(open: boolean, anchor: EventTarget | null, onOutside: () => void): void {
    const listening = open && this.isDesktop;
    if (listening && !this.detachOutsideClick) this.detachOutsideClick = attachOutsideClick([this, anchor], onOutside);
    if (!listening && this.detachOutsideClick) {
      this.detachOutsideClick();
      this.detachOutsideClick = null;
    }
  }
}

/** Defines a chrome element once (a module is evaluated once per page, but tests re-import). */
export function defineChromeElement(tag: string, ctor: CustomElementConstructor): void {
  if (!customElements.get(tag)) customElements.define(tag, ctor);
}

/** The pointer-down handler of most DOM controls: the click cue and a soft haptic through the chrome root. */
export function pressFeedback(event: Event): void {
  dispatchChromePress(event.currentTarget as EventTarget, { audio: "ui.click", haptic: "soft" });
}

/** The click cue alone (the popups' close buttons, as authored). */
export function pressAudio(event: Event): void {
  dispatchChromePress(event.currentTarget as EventTarget, { audio: "ui.click" });
}

/** The soft haptic alone (the bet steps play their own cue through the bet presenter). */
export function pressHaptic(event: Event): void {
  dispatchChromePress(event.currentTarget as EventTarget, { haptic: "soft" });
}

